import { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLiving } from '../../contexts/LivingContext';
import { api } from '../../lib/api';

const REFRESH_MS = 15 * 60 * 1000; // backend caches /weather for 10 min
const MAX_WEATHER_AGE_MS = 30 * 60 * 1000;
const WMO_CODES = new Set([0, 1, 2, 3, 45, 48, 51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 71, 73, 75, 77, 80, 81, 82, 85, 86, 95, 96, 99]);
const EMPTY_WEATHER = {
    weatherCode: null, wind: null, gusts: null, cloud: null, temp: null,
    weatherUpdatedAt: null,
};
const measurement = (value) => Number.isFinite(value) ? value : null;

/** Feeds the user's location + current weather into the living engine. */
export default function AmbientSync() {
    const { user } = useAuth();
    const { ambient, setAmbient } = useLiving();
    const lat = user?.location?.lat ?? ambient.lat;
    const lon = user?.location?.lon ?? ambient.lon;
    const ambientRef = useRef(ambient);
    ambientRef.current = ambient;

    useEffect(() => {
        let cancelled = false;
        let requestSequence = 0;
        let activeController;
        const commit = (patch) => {
            ambientRef.current = { ...ambientRef.current, ...patch };
            setAmbient(patch);
        };
        const clearWeather = () => commit({ lat, lon, ...EMPTY_WEATHER });
        const pull = async () => {
            if (document.hidden) return;
            const sequence = ++requestSequence;
            activeController?.abort();
            const controller = new AbortController();
            activeController = controller;
            const isCurrent = () => !cancelled && sequence === requestSequence && !controller.signal.aborted;

            const current = ambientRef.current;
            const age = Date.now() - current.weatherUpdatedAt;
            if (current.lat !== lat || current.lon !== lon
                || !Number.isFinite(current.weatherUpdatedAt) || current.weatherUpdatedAt <= 0
                || age < 0 || age > MAX_WEATHER_AGE_MS) clearWeather();

            try {
                const response = await api.get('/weather', { params: { lat, lon }, signal: controller.signal });
                if (!isCurrent()) return;
                const cur = response.data?.current;
                if (!WMO_CODES.has(cur?.weather_code)) {
                    clearWeather();
                    return;
                }
                commit({
                    lat, lon,
                    weatherCode: cur.weather_code,
                    wind: measurement(cur.wind_speed_10m),
                    gusts: measurement(cur.wind_gusts_10m),
                    cloud: Number.isFinite(cur.cloud_cover) ? Math.min(100, Math.max(0, cur.cloud_cover)) : null,
                    temp: measurement(cur.temperature_2m),
                    weatherUpdatedAt: Date.now(),
                });
            } catch {
                if (isCurrent()) clearWeather();
            }
        };
        pull();
        const id = setInterval(pull, REFRESH_MS);
        document.addEventListener('visibilitychange', pull);
        return () => {
            cancelled = true;
            activeController?.abort();
            clearInterval(id);
            document.removeEventListener('visibilitychange', pull);
        };
    }, [lat, lon, setAmbient]);

    return null;
}
